import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from './layout/Layout';
import { syncthingApi } from '../services/api';
import './SyncthingPanel.css';

// StudioAEye Sync (M020-F0001 / M020-F0002)
//
// M020-F0001: the server exposes its own stable Syncthing device ID so users
// can pair official Syncthing clients to it.
// M020-F0002: users register their OWN device(s) by Syncthing device ID,
// private per user, no double-claim.
//
// M004-F0660 / M004-F0661 (WebUI #125, #126): folder-link approval, the
// root-share exposure tree, per-folder shares and the per-user instance
// status/provisioning are NOT implemented on this panel. They ARE built and
// deployed - on the VideoLib service's own web UI at /sync/ui (M020-D001
// section 12.1; interfaces/videolib-sync-management/v1, all twelve operations
// provider-implemented). This panel keeps device registration only and links
// out; it deliberately shows no "approve folder" UI of its own. Which surface
// owns device management long term is an open owner decision (WebUI #127) and
// is not decided here.

// Same-origin path served by the VideoLib service behind the edge proxy.
// A plain anchor (full navigation), never a router <Link>: the SPA catch-all
// would swallow the path.
export const SYNC_UI_PATH = '/sync/ui';

// RFC4648 base32 alphabet without padding (A-Z, 2-7), 8 groups of 7 chars,
// hyphen-separated - the exact shape of a Syncthing device ID.
const DEVICE_ID_PATTERN = /^[A-Z2-7]{7}(-[A-Z2-7]{7}){7}$/;

const getPayload = (response) => response.data?.data || response.data || {};

// Mirrors the extraction path already used by BotSessionKeysPanel /
// api.js consumers of similar POST endpoints: prefer the backend's
// structured error.message, then a flatter message, then the axios error.
const getErrorMessage = (err, fallback) => (
  err.response?.data?.error?.message
  || err.response?.data?.message
  || err.response?.data?.error
  || err.message
  || fallback
);

const normalizeDeviceIdInput = (value) => value.trim().toUpperCase();

const isValidDeviceId = (value) => DEVICE_ID_PATTERN.test(value);

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ServerDeviceSection = ({ testId }) => {
  const [deviceId, setDeviceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const fetchServerDevice = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const payload = getPayload(await syncthingApi.getServerDevice());
      setDeviceId(payload.deviceId || null);
    } catch (err) {
      setDeviceId(null);
      setError(getErrorMessage(err, 'Failed to load the server device ID.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServerDevice();
  }, [fetchServerDevice]);

  const handleCopy = async () => {
    if (!deviceId || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(deviceId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard access can be denied by the browser; not fatal.
      setError('Could not copy automatically. Select the device ID and copy it manually.');
    }
  };

  return (
    <section className="profile-panel sync-server-panel" data-testid={testId}>
      <div className="profile-panel__header">
        <div>
          <h3>Server device</h3>
          <p>Install Syncthing, add this device below, then share a folder with it in the Syncthing app.</p>
        </div>
      </div>

      {loading ? (
        <div className="loading-container" data-testid={`${testId}-loading`}>
          <div className="spinner"></div>
        </div>
      ) : error ? (
        <div className="message error" data-testid={`${testId}-error`}>{error}</div>
      ) : deviceId ? (
        <div className="sync-server-device">
          <code className="sync-device-id" data-testid={`${testId}-device-id`}>{deviceId}</code>
          <button type="button" className="btn btn-outline" onClick={handleCopy} data-testid={`${testId}-copy`}>
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      ) : (
        <p className="form-hint" data-testid={`${testId}-unconfigured`}>
          Sync isn&apos;t set up on the server yet. Check back later.
        </p>
      )}

      <p className="form-hint">
        <a href="https://syncthing.net/downloads/" target="_blank" rel="noopener noreferrer">
          Download Syncthing
        </a>
        {' '}and add this as a remote device.
      </p>
    </section>
  );
};

const emptyRegisterForm = () => ({ deviceId: '', label: '' });

const MyDevicesSection = ({ testId, announce }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [registerForm, setRegisterForm] = useState(emptyRegisterForm);
  const [registerFieldError, setRegisterFieldError] = useState('');
  const [editingId, setEditingId] = useState('');
  const [editLabel, setEditLabel] = useState('');

  const loadDevices = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const payload = getPayload(await syncthingApi.listMyDevices());
      const list = Array.isArray(payload) ? payload : (payload.devices || []);
      setDevices(list);
    } catch (err) {
      setDevices([]);
      setError(getErrorMessage(err, 'Failed to load your devices.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleRegister = async (event) => {
    event.preventDefault();
    setRegisterFieldError('');

    // Users will likely paste a lowercase or mixed-case ID; Syncthing itself
    // displays IDs uppercase, so normalize before validating/submitting.
    const normalizedId = normalizeDeviceIdInput(registerForm.deviceId);

    if (!isValidDeviceId(normalizedId)) {
      setRegisterFieldError('Enter a valid Syncthing device ID: 8 groups of 7 characters (A-Z, 2-7), separated by hyphens.');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      const payload = getPayload(await syncthingApi.registerDevice(normalizedId, registerForm.label.trim()));
      setDevices((current) => [...current, payload]);
      setRegisterForm(emptyRegisterForm());
      announce('Device added');
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to register device.');
      setError(message);
      announce(message);
    } finally {
      setActionLoading(false);
    }
  };

  const startEdit = (device) => {
    setEditingId(device.id);
    setEditLabel(device.label || '');
    setError('');
  };

  const cancelEdit = () => {
    setEditingId('');
    setEditLabel('');
  };

  const handleRename = async (event, device) => {
    event.preventDefault();
    try {
      setActionLoading(true);
      setError('');
      const payload = getPayload(await syncthingApi.renameDevice(device.id, editLabel.trim()));
      setDevices((current) => current.map((item) => (item.id === device.id ? payload : item)));
      setEditingId('');
      setEditLabel('');
      announce('Device renamed');
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to rename device.');
      setError(message);
      announce(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemove = async (device) => {
    if (!window.confirm(`Remove ${device.label || device.deviceId} from your devices?`)) {
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await syncthingApi.removeDevice(device.id);
      setDevices((current) => current.filter((item) => item.id !== device.id));
      announce('Device removed');
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to remove device.');
      setError(message);
      announce(message);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <section className="profile-panel sync-devices-panel" data-testid={testId}>
      <div className="profile-panel__header">
        <div>
          <h3>My devices</h3>
          <p>Register your own Syncthing device IDs. Devices you add here are private to your account.</p>
        </div>
        <button type="button" className="btn btn-outline" onClick={loadDevices} disabled={loading || actionLoading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error ? <div className="message error" data-testid={`${testId}-error`}>{error}</div> : null}

      <div className="sync-device-list" data-testid={`${testId}-list`}>
        {loading ? (
          <div className="loading-container" data-testid={`${testId}-loading`}>
            <div className="spinner"></div>
          </div>
        ) : devices.length === 0 ? (
          <p className="form-hint" data-testid={`${testId}-empty`}>No devices registered yet.</p>
        ) : (
          devices.map((device) => {
            const isEditing = editingId === device.id;
            const updatedLabel = formatDateTime(device.updatedAt);
            return (
              <article key={device.id} className="sync-device-card" data-testid={`${testId}-card`}>
                <div className="sync-device-card-main">
                  <code className="sync-device-id sync-device-id-small">{device.deviceId}</code>
                  {isEditing ? (
                    <form className="sync-inline-edit-form" onSubmit={(event) => handleRename(event, device)}>
                      <label htmlFor={`sync-device-edit-${device.id}`} className="sr-only">
                        Device name
                      </label>
                      <input
                        id={`sync-device-edit-${device.id}`}
                        className="form-input"
                        value={editLabel}
                        onChange={(event) => setEditLabel(event.target.value)}
                        maxLength={120}
                      />
                      <button type="submit" className="btn btn-primary" disabled={actionLoading}>Save</button>
                      <button type="button" className="btn btn-outline" onClick={cancelEdit} disabled={actionLoading}>Cancel</button>
                    </form>
                  ) : (
                    <strong>{device.label || 'Unlabelled device'}</strong>
                  )}
                  {updatedLabel ? <span className="sync-device-meta">Updated {updatedLabel}</span> : null}
                </div>
                {!isEditing ? (
                  <div className="sync-device-actions">
                    <button type="button" className="btn btn-outline" onClick={() => startEdit(device)} disabled={actionLoading}>
                      Rename
                    </button>
                    <button type="button" className="btn btn-danger" onClick={() => handleRemove(device)} disabled={actionLoading}>
                      Remove
                    </button>
                  </div>
                ) : null}
              </article>
            );
          })
        )}
      </div>

      <form className="sync-register-form" onSubmit={handleRegister} data-testid={`${testId}-register-form`}>
        <h4>Add a device</h4>
        {registerFieldError ? (
          <div className="message error" data-testid={`${testId}-register-field-error`}>{registerFieldError}</div>
        ) : null}
        <div className="form-group">
          <label htmlFor="sync-register-device-id">Device ID</label>
          <input
            id="sync-register-device-id"
            className="form-input"
            value={registerForm.deviceId}
            onChange={(event) => setRegisterForm((current) => ({ ...current, deviceId: event.target.value }))}
            placeholder="XXXXXXX-XXXXXXX-XXXXXXX-XXXXXXX-XXXXXXX-XXXXXXX-XXXXXXX-XXXXXXX"
            required
          />
          <span className="form-hint">Find this in your Syncthing app under Actions &gt; Show ID.</span>
        </div>
        <div className="form-group">
          <label htmlFor="sync-register-label">Name</label>
          <input
            id="sync-register-label"
            className="form-input"
            value={registerForm.label}
            onChange={(event) => setRegisterForm((current) => ({ ...current, label: event.target.value }))}
            placeholder="My laptop"
            maxLength={120}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={actionLoading}>
          {actionLoading ? 'Saving...' : 'Add device'}
        </button>
      </form>
    </section>
  );
};

const SyncthingPanel = ({ embedded = false }) => {
  const [status, setStatus] = useState('');

  const announce = useCallback((message) => {
    setStatus(message);
  }, []);

  const testId = useMemo(() => 'syncthing-panel', []);

  const content = (
    <div className="sync-panel-page" data-testid={testId}>
      <header className="page-header">
        <h1>Sync</h1>
        <p className="subtitle">Pair your own Syncthing devices with StudioAEye Sync.</p>
      </header>

      <div aria-live="polite" className="sr-only" data-testid={`${testId}-status`}>
        {status}
      </div>

      <ServerDeviceSection testId={`${testId}-server`} />
      <MyDevicesSection testId={`${testId}-devices`} announce={announce} />

      <section className="sync-manage-link" data-testid={`${testId}-manage`}>
        <p className="form-hint">
          Your sync status, folder sharing and approving or rejecting folder offers
          are managed in StudioAEye Sync.
        </p>
        <a
          className="btn btn-outline"
          href={SYNC_UI_PATH}
          data-testid={`${testId}-open-sync-ui`}
        >
          Open StudioAEye Sync
        </a>
      </section>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout>
      {content}
    </Layout>
  );
};

export default SyncthingPanel;
